// Production-safe logging utility
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  action?: string;
  [key: string]: any;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;

  private log(level: LogLevel, message: string, context?: LogContext) {
    if (!this.isDevelopment && level === 'debug') {
      return; // Skip debug logs in production
    }

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
    
    if (context) {
      console[level](`${prefix} ${message}`, context);
    } else {
      console[level](`${prefix} ${message}`);
    }
  }

  debug(message: string, context?: LogContext) {
    this.log('debug', message, context);
  }

  info(message: string, context?: LogContext) {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log('warn', message, context);
  }

  error(message: string, context?: LogContext) {
    this.log('error', message, context);
  }

  /**
   * Log error with optional security system integration
   * @param message Error message
   * @param context Additional context
   * @param reportToSecurity Whether to report to security monitoring (production only)
   */
  async errorWithSecurity(
    message: string, 
    context?: LogContext & { error?: Error },
    reportToSecurity: boolean = true
  ) {
    this.error(message, context);

    // Only report to security in production if enabled
    if (!this.isDevelopment && reportToSecurity && context?.error) {
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        
        const errorDetails = {
          error_type: 'application_error',
          error_message: message,
          error_name: context.error.name,
          stack_trace: context.error.stack,
          component: context.component,
          action: context.action,
          route: window.location.pathname,
          timestamp: new Date().toISOString(),
          ...context
        };

        await supabase.rpc('log_security_event', {
          p_event_type: 'application_error',
          p_resource_type: 'error',
          p_resource_id: null,
          p_organization_id: null,
          p_details: JSON.stringify(errorDetails),
          p_risk_level: 'medium'
        });
      } catch (err) {
        console.error('[Logger] Failed to report error to security system:', err);
      }
    }
  }
}

export const logger = new Logger();